import { initializeApp } from "firebase/app";
import {
  getAuth,
  signOut,
  setPersistence,
  browserSessionPersistence,
  onAuthStateChanged
} from "firebase/auth";
// import { getStorage } from "firebase/storage";
// import { collection, getFirestore } from "firebase/firestore";

// Firebase configuration constants - explicitly named for clarity and auditability
const FIREBASE_API_KEY = import.meta.env.VITE_API_KEY;
const FIREBASE_AUTH_DOMAIN = import.meta.env.VITE_AUTH_DOMAIN;
const FIREBASE_PROJECT_ID = import.meta.env.VITE_PROJECT_ID;
const FIREBASE_STORAGE_BUCKET = import.meta.env.VITE_STORAGE_BUCKET;
const FIREBASE_MESSAGING_SENDER_ID = import.meta.env.VITE_MESSAGING_SENDER_ID;
const FIREBASE_APP_ID = import.meta.env.VITE_APP_ID;

const firebaseConfig = {
  apiKey: FIREBASE_API_KEY,
  authDomain: FIREBASE_AUTH_DOMAIN,
  projectId: FIREBASE_PROJECT_ID,
  storageBucket: FIREBASE_STORAGE_BUCKET,
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
  appId: FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);

const auth = getAuth(app);
setPersistence(auth, browserSessionPersistence);

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const OUT_OF_FOCUS_TIMEOUT = 15 * 60 * 1000; // 15 minutes
let inactivityTimer: NodeJS.Timeout | null = null;
let outOfFocusTimer: NodeJS.Timeout | null = null;
let lastActivityTime = Date.now();

const resetInactivityTimer = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }

  lastActivityTime = Date.now();

  inactivityTimer = setTimeout(() => {
    signOut(auth);
  }, INACTIVITY_TIMEOUT);
};

const handleWindowFocus = () => {
  if (outOfFocusTimer) {
    clearTimeout(outOfFocusTimer);
    outOfFocusTimer = null;
  }

  // Check if too much time passed while out of focus
  const timeSinceLastActivity = Date.now() - lastActivityTime;
  if (timeSinceLastActivity > OUT_OF_FOCUS_TIMEOUT) {
    signOut(auth);
    return;
  }

  resetInactivityTimer();
};

const handleWindowBlur = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }

  outOfFocusTimer = setTimeout(() => {
    signOut(auth);
  }, OUT_OF_FOCUS_TIMEOUT);
};

const setupInactivityLogout = () => {
  const activities = ["click", "keypress", "scroll", "touchstart"];

  const handleActivity = () => {
    // Only reset if window is focused
    if (document.hasFocus()) {
      resetInactivityTimer();
    }
  };

  activities.forEach((activity) => {
    document.addEventListener(activity, handleActivity, true);
  });

  window.addEventListener("focus", handleWindowFocus);
  window.addEventListener("blur", handleWindowBlur);

  // Start the timer
  resetInactivityTimer();

  return () => {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (outOfFocusTimer) clearTimeout(outOfFocusTimer);

    activities.forEach((activity) => {
      document.removeEventListener(activity, handleActivity, true);
    });

    window.removeEventListener("focus", handleWindowFocus);
    window.removeEventListener("blur", handleWindowBlur);
  };
};

// Track cleanup function to prevent race conditions and memory leaks
// when auth state changes multiple times
let inactivityCleanup: (() => void) | null = null;
// Guard flag to prevent concurrent setup during rapid auth state changes
let isSettingUpInactivity = false;

// Setup inactivity logout when user is authenticated
onAuthStateChanged(auth, (user) => {
  // Prevent concurrent setup - if already setting up, skip this callback
  // This handles rapid auth state changes (e.g., quick login/logout cycles)
  if (isSettingUpInactivity) {
    return;
  }

  // Always clean up previous session timers and listeners first
  // This prevents accumulation of event listeners and timer conflicts
  if (inactivityCleanup) {
    inactivityCleanup();
    inactivityCleanup = null;
  }

  // Setup new session timers only if user is authenticated
  if (user) {
    isSettingUpInactivity = true;
    try {
      inactivityCleanup = setupInactivityLogout();
    } finally {
      isSettingUpInactivity = false;
    }
  }
});

export { auth, signOut };
